import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Clock, PartyPopper } from "lucide-react";

type ClaimState =
  | { kind: "loading" }
  | { kind: "offer" }
  | { kind: "confirmed"; bookingId?: string }
  | { kind: "error"; message: string };

// Public page reached from a waitlist-offer link (DM or email):
//   /waitlist/claim/:token
// It calls the waitlist-claim edge function. Manual/free listings are booked
// immediately; Stripe-required listings are redirected into the normal
// prefilled storefront checkout (which holds the slot until paid).
//
// The token is validated on LOAD (action:"peek") before any celebration is
// shown. An expired/used/forwarded link lands straight on its real state
// instead of "you're first in line" followed by a failing click (QA F-A6-1;
// mirrors the make-up claim page).
export default function WaitlistClaim() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<ClaimState>({ kind: "loading" });
  const [claiming, setClaiming] = useState(false);

  // Shared response handling. Peek and claim return the same shapes (peek
  // adds mode:"offer"). Also tolerates a not-yet-redeployed fn that ignores
  // action:"peek" and claims immediately: those confirmed/redirect responses
  // are honored rather than mislabelled as errors.
  const applyResponse = useCallback(async (data: any, error: unknown, fallbackMsg: string) => {
    if (error) {
      // Edge function returns a JSON body with `error` on non-2xx.
      let msg = fallbackMsg;
      try {
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
          const body = await ctx.json();
          if (body?.error) msg = body.error;
        }
      } catch { /* keep default */ }
      setState({ kind: "error", message: msg });
      return;
    }
    if (data?.mode === "redirect_payment" && data.url) {
      window.location.href = data.url;
      return;
    }
    if (data?.mode === "confirmed") {
      setState({ kind: "confirmed", bookingId: data.booking_id });
      return;
    }
    if (data?.mode === "offer") {
      setState({ kind: "offer" });
      return;
    }
    setState({ kind: "error", message: data?.error || "This offer is no longer available." });
  }, []);

  // Validate the token on load. Never celebrate an invalid link.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ kind: "error", message: "This claim link is invalid." });
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke("waitlist-claim", {
          body: { offer_token: token, action: "peek" },
        });
        if (cancelled) return;
        await applyResponse(data, error, "This offer link is no longer valid.");
      } catch (e) {
        if (!cancelled) setState({ kind: "error", message: (e as Error).message });
      }
    })();
    return () => { cancelled = true; };
  }, [token, applyResponse]);

  const claim = useCallback(async () => {
    if (!token) {
      setState({ kind: "error", message: "This claim link is invalid." });
      return;
    }
    setClaiming(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-claim", {
        body: { offer_token: token },
      });
      await applyResponse(data, error, "We couldn't claim this spot. It may have just been taken.");
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    } finally {
      setClaiming(false);
    }
  }, [token, applyResponse]);

  // Auto-redirect a confirmed booking to the confirmation page after a beat.
  useEffect(() => {
    if (state.kind === "confirmed" && state.bookingId) {
      const t = setTimeout(() => navigate(`/booking-confirmed/${state.bookingId}`), 1800);
      return () => clearTimeout(t);
    }
  }, [state, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md bg-background rounded-2xl shadow-sm border p-8 text-center">
        {state.kind === "loading" && (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Checking your offer…</p>
          </>
        )}

        {state.kind === "offer" && (
          <>
            <PartyPopper className="w-10 h-10 mx-auto mb-4 text-primary" />
            <h1 className="text-xl font-semibold mb-2">A spot opened up!</h1>
            <p className="text-muted-foreground mb-6">
              You're first in line. Confirm now to lock it in before it rolls to the next person.
            </p>
            <Button className="w-full" size="lg" onClick={claim} disabled={claiming}>
              {claiming ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Claiming…</>
              ) : (
                <>Claim my spot</>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" /> This offer is time-limited.
            </p>
          </>
        )}

        {state.kind === "confirmed" && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold mb-2">You're booked! 🎉</h1>
            <p className="text-muted-foreground">Taking you to your confirmation…</p>
          </>
        )}

        {state.kind === "error" && (
          <>
            <h1 className="text-xl font-semibold mb-2">Spot no longer available</h1>
            <p className="text-muted-foreground mb-6">{state.message}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">Back to home</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
